import { forwardRef, type ReactNode } from 'react'
import { Slider as BaseSlider } from '@base-ui/react/slider'
import { cn } from '@/lib/utils'
import './slider.css'

export type SliderProps = Omit<BaseSlider.Root.Props<number>, 'className' | 'children' | 'render'> & {
  /** Visible label. It also names the slider for assistive technology. */
  label: ReactNode
  /**
   * Keeps the label as the slider's name but out of view, for a control that already says what it sets, such as a
   * slider beside a speaker button.
   */
  hideLabel?: boolean
  /** Shows the formatted value beside the label. */
  showValue?: boolean
  /** The value as read by assistive technology, when the formatted value isn't enough. */
  getAriaValueText?: (formattedValue: string, value: number) => string
  className?: string
}

/**
 * A value people set by dragging along a range, such as a volume. The change applies as it moves (`onValueChange`);
 * `onValueCommitted` fires once the drag or key press ends, for work that shouldn't run on every step. Pass `format`
 * to write the value with a unit, such as `{ style: 'unit', unit: 'percent' }`.
 */
export const Slider = forwardRef<HTMLDivElement, SliderProps>(function Slider(
  { label, hideLabel = false, showValue = true, getAriaValueText, className, ...root },
  ref,
) {
  return (
    <BaseSlider.Root ref={ref} className={cn('play-slider', className)} {...root}>
      <div className={cn('play-slider__header', hideLabel && !showValue && 'sr-only')}>
        <BaseSlider.Label className={cn('play-slider__label', hideLabel && 'sr-only')}>{label}</BaseSlider.Label>
        {showValue && <BaseSlider.Value className="play-slider__value" />}
      </div>
      <BaseSlider.Control className="play-slider__control">
        <BaseSlider.Track className="play-slider__track">
          <BaseSlider.Indicator className="play-slider__indicator" />
          <BaseSlider.Thumb className="play-slider__thumb" getAriaValueText={getAriaValueText} />
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  )
})
